import type { HorizontalBounds } from "../geometry.ts";
import type { BuildingFootprintData } from "./tile.ts";

export const DISTRICT_VISUAL_SAMPLE_SCHEMA_VERSION = 1 as const;

export type DistrictVisualStyle = "soviet_panel";

export interface DistrictVisualSample {
  readonly schemaVersion: typeof DISTRICT_VISUAL_SAMPLE_SCHEMA_VERSION;
  readonly id: string;
  readonly label: string;
  readonly districtStyle: DistrictVisualStyle;
  readonly sourceId: string;
  readonly sourceBoundsLks94: {
    readonly crs: "EPSG:3346";
    readonly minEasting: number;
    readonly minNorthing: number;
    readonly maxEasting: number;
    readonly maxNorthing: number;
  };
  /**
   * A presentation-only local frame. Horizontal shape, orientation, and
   * distance remain source-derived; the sample does not claim measured terrain
   * or building height.
   */
  readonly localBounds: HorizontalBounds;
  readonly verticalBasis: "flat-style-benchmark";
  readonly viewpoint: {
    readonly local: {
      readonly x: number;
      readonly z: number;
    };
    readonly presentationLocal: {
      readonly x: number;
      readonly z: number;
    };
    readonly sourceReferenceId: string;
    readonly role: "licensed-reference-camera-position";
    readonly presentationMethod: string;
  };
  readonly buildings: readonly BuildingFootprintData[];
}

export function parseDistrictVisualSample(value: unknown): DistrictVisualSample {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("District visual sample must be an object");
  }
  const sample = value as Record<string, unknown>;
  if (sample.schemaVersion !== DISTRICT_VISUAL_SAMPLE_SCHEMA_VERSION) {
    throw new Error("District visual sample schemaVersion must equal 1");
  }
  requireString(sample.id, "sample.id");
  requireString(sample.label, "sample.label");
  if (sample.districtStyle !== "soviet_panel") {
    throw new Error("sample.districtStyle must equal soviet_panel");
  }
  requireString(sample.sourceId, "sample.sourceId");
  const sourceBounds = requireRecord(
    sample.sourceBoundsLks94,
    "sample.sourceBoundsLks94",
  );
  if (sourceBounds.crs !== "EPSG:3346") {
    throw new Error("sample.sourceBoundsLks94.crs must equal EPSG:3346");
  }
  for (const field of [
    "minEasting",
    "minNorthing",
    "maxEasting",
    "maxNorthing",
  ]) {
    requireFinite(sourceBounds[field], `sample.sourceBoundsLks94.${field}`);
  }
  const localBounds = requireRecord(sample.localBounds, "sample.localBounds");
  for (const field of ["minX", "minZ", "maxX", "maxZ"]) {
    requireFinite(localBounds[field], `sample.localBounds.${field}`);
  }
  if (sample.verticalBasis !== "flat-style-benchmark") {
    throw new Error(
      "sample.verticalBasis must disclose the flat-style-benchmark basis",
    );
  }
  const viewpoint = requireRecord(sample.viewpoint, "sample.viewpoint");
  const viewpointLocal = requireRecord(
    viewpoint.local,
    "sample.viewpoint.local",
  );
  requireFinite(viewpointLocal.x, "sample.viewpoint.local.x");
  requireFinite(viewpointLocal.z, "sample.viewpoint.local.z");
  const presentationLocal = requireRecord(
    viewpoint.presentationLocal,
    "sample.viewpoint.presentationLocal",
  );
  requireFinite(
    presentationLocal.x,
    "sample.viewpoint.presentationLocal.x",
  );
  requireFinite(
    presentationLocal.z,
    "sample.viewpoint.presentationLocal.z",
  );
  requireString(
    viewpoint.sourceReferenceId,
    "sample.viewpoint.sourceReferenceId",
  );
  if (viewpoint.role !== "licensed-reference-camera-position") {
    throw new Error(
      "sample.viewpoint.role must disclose the licensed reference position",
    );
  }
  requireString(
    viewpoint.presentationMethod,
    "sample.viewpoint.presentationMethod",
  );
  if (!Array.isArray(sample.buildings) || sample.buildings.length === 0) {
    throw new Error("sample.buildings must be a non-empty array");
  }
  for (const [index, rawBuilding] of sample.buildings.entries()) {
    const building = requireRecord(
      rawBuilding,
      `sample.buildings[${index}]`,
    );
    requireString(building.id, `sample.buildings[${index}].id`);
    requireString(
      building.sourceFootprintId,
      `sample.buildings[${index}].sourceFootprintId`,
    );
    requireString(building.sourceId, `sample.buildings[${index}].sourceId`);
    requireFinite(
      building.baseElevationM,
      `sample.buildings[${index}].baseElevationM`,
    );
    const footprint = requireRecord(
      building.footprint,
      `sample.buildings[${index}].footprint`,
    );
    requireRing(footprint.outer, `sample.buildings[${index}].footprint.outer`);
    if (!Array.isArray(footprint.holes)) {
      throw new Error(
        `sample.buildings[${index}].footprint.holes must be an array`,
      );
    }
    for (const [holeIndex, hole] of footprint.holes.entries()) {
      requireRing(
        hole,
        `sample.buildings[${index}].footprint.holes[${holeIndex}]`,
      );
    }
  }
  return value as DistrictVisualSample;
}

function requireRecord(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Record<string, unknown>;
}

function requireString(value: unknown, path: string): asserts value is string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`${path} must be a non-empty string`);
  }
}

function requireFinite(value: unknown, path: string): asserts value is number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`${path} must be finite`);
  }
}

function requireRing(value: unknown, path: string): void {
  if (!Array.isArray(value) || value.length < 4) {
    throw new Error(`${path} must contain at least four points`);
  }
  for (const [index, rawPoint] of value.entries()) {
    const point = requireRecord(rawPoint, `${path}[${index}]`);
    requireFinite(point.x, `${path}[${index}].x`);
    requireFinite(point.z, `${path}[${index}].z`);
  }
}
